import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { Plus, Pencil, Trash2, Power, X, Store } from "lucide-react";
import DataTable from "../../../components/DataTable";
import ConfirmationModal from "../../../components/ConfirmationModal";
import {
  searchReferralVendors,
  createReferralVendor,
  updateReferralVendor,
  deleteReferralVendor,
  listReferralVendorCategories,
} from "../../../services/referralAdmin";

const EMPTY = {
  name: "",
  description: "",
  logoUrl: "",
  contactPerson: "",
  email: "",
  phone: "",
  websiteUrl: "",
  isActive: true,
  categoryIds: [],
};

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">{label}</label>
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
    />
  </div>
);

const ReferralVendorsTab = () => {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vendorsRes, catsRes] = await Promise.all([
        searchReferralVendors({}),
        listReferralVendorCategories(true),
      ]);
      setRows(Array.isArray(vendorsRes) ? vendorsRes : vendorsRes?.items || []);
      setCategories(Array.isArray(catsRes) ? catsRes : catsRes?.items || []);
    } catch (e) {
      toast.error("Failed to load referral vendors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name || "",
      description: v.description || "",
      logoUrl: v.logoUrl || "",
      contactPerson: v.contactPerson || "",
      email: v.email || "",
      phone: v.phone || "",
      websiteUrl: v.websiteUrl || "",
      isActive: v.isActive,
      categoryIds: (v.categories || []).map((c) => c.id),
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Vendor name is required");
    setSaving(true);
    try {
      if (editing) {
        await updateReferralVendor(editing.id, form);
        toast.success("Vendor updated");
      } else {
        await createReferralVendor(form);
        toast.success("Vendor created");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save vendor");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (v) => {
    try {
      await updateReferralVendor(v.id, {
        name: v.name,
        description: v.description,
        logoUrl: v.logoUrl,
        contactPerson: v.contactPerson,
        email: v.email,
        phone: v.phone,
        websiteUrl: v.websiteUrl,
        isActive: !v.isActive,
        categoryIds: (v.categories || []).map((c) => c.id),
      });
      await load();
    } catch (e) {
      toast.error("Failed to update vendor status");
    }
  };

  const doDelete = async () => {
    const id = confirm?.id;
    setConfirm(null);
    try {
      await deleteReferralVendor(id);
      toast.success("Vendor deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const columns = [
    {
      key: "name",
      header: "Reward Partner",
      width: 240,
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{r.name}</p>
          <p className="text-xs text-slate-500 truncate">{r.websiteUrl || r.email || "—"}</p>
        </div>
      ),
    },
    {
      key: "categories",
      header: "Categories",
      width: 260,
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.categories || []).length === 0 ? (
            <span className="text-slate-400 text-sm">—</span>
          ) : (
            (r.categories || []).map((c) => (
              <span key={c.id} className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 border border-slate-200">
                {c.name}
              </span>
            ))
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: 120,
      render: (r) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${r.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
          {r.isActive ? "ACTIVE" : "INACTIVE"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: 150,
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center gap-1.5">
          <button title="Edit" onClick={() => openEdit(r)} className="w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 inline-flex items-center justify-center">
            <Pencil size={15} />
          </button>
          <button title={r.isActive ? "Deactivate" : "Activate"} onClick={() => toggleActive(r)} className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${r.isActive ? "text-amber-500 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
            <Power size={15} />
          </button>
          <button title="Delete" onClick={() => setConfirm({ id: r.id, name: r.name })} className="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Reward partners where buyers redeem referral rewards. Products are not listed.</p>
        <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Reward Partner
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        minWidth={880}
        rowKey={(r, i) => r?.id ?? i}
        emptyState={
          <div className="px-6 py-12 text-center">
            <Store size={28} className="mx-auto text-slate-300" />
            <p className="text-base font-semibold text-slate-900 mt-2">No reward partners yet</p>
            <p className="text-sm text-slate-500 mt-1">Add a reward partner and assign the categories they serve.</p>
          </div>
        }
      />

      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${modalOpen ? "" : "hidden"}`}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">{editing ? "Edit Reward Partner" : "Add Reward Partner"}</h3>
            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. MegaMart" />
            <Field label="Website" value={form.websiteUrl} onChange={(v) => setForm({ ...form, websiteUrl: v })} />
            <Field label="Contact Person" value={form.contactPerson} onChange={(v) => setForm({ ...form, contactPerson: v })} />
            <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <Field label="Logo URL" value={form.logoUrl} onChange={(v) => setForm({ ...form, logoUrl: v })} />
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600">Categories served</label>
              <Select
                isMulti
                options={categoryOptions}
                value={categoryOptions.filter((o) => (form.categoryIds || []).includes(o.value))}
                onChange={(opts) => setForm({ ...form, categoryIds: (opts || []).map((o) => o.value) })}
                placeholder="Select categories"
                className="mt-1 react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-slate-600">Description</label>
              <textarea
                value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button onClick={() => setModalOpen(false)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
            <button onClick={save} disabled={saving} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
        message={`Delete reward partner "${confirm?.name}"?`}
      />
    </div>
  );
};

export default ReferralVendorsTab;
